/* src/pages/articles/SelfCustody.jsx */
import React from "react";
import { Link } from "react-router-dom";
import { Head } from "@unhead/react";

export default function SelfCustodyArticle() {
  const title =
    "Bitcoin Self-Custody: What It Means & How to Get Started | SatStacker";

  const description =
    "Learn what Bitcoin self-custody means, how private keys and recovery phrases work, the tradeoffs of holding your own Bitcoin, and how to get started safely.";

  const canonical =
    "https://satstacker.app/articles/self-custody";

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline:
      "Bitcoin Self-Custody: What It Means & How to Get Started",
    description,
    mainEntityOfPage: canonical,
    author: {
      "@type": "Organization",
      name: "SatStacker",
      url: "https://satstacker.app",
    },
    publisher: {
      "@type": "Organization",
      name: "SatStacker",
      url: "https://satstacker.app",
    },
    dateModified: "2026-09-22",
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "SatStacker",
        item: "https://satstacker.app/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Bitcoin Self-Custody",
        item: canonical,
      },
    ],
  };

  const faqItems = [
    {
      q: "What does self-custody mean in Bitcoin?",
      a: "Bitcoin self-custody means you control the private keys needed to spend the Bitcoin associated with your wallet. You are not relying on an exchange or custodian to authorize withdrawals for you.",
    },
    {
      q: "What is a recovery phrase?",
      a: "A recovery phrase is a backup that can restore access to a compatible Bitcoin wallet. Anyone who gets the phrase may be able to control the wallet, so it should be protected carefully and never shared casually.",
    },
    {
      q: "Is self-custody safer than leaving Bitcoin on an exchange?",
      a: "Self-custody removes some forms of third-party risk, but it also gives you more responsibility. Losing access to your keys or recovery backup can make funds difficult or impossible to recover.",
    },
    {
      q: "Do I need a hardware wallet for self-custody?",
      a: "Not necessarily. Self-custody can be done with software or hardware wallets. Hardware wallets are often used for larger or longer-term holdings because they can keep signing keys isolated from general-purpose devices.",
    },
    {
      q: "Does SatStacker custody my Bitcoin?",
      a: "SatStacker is designed as a non-custodial automation layer. It connects to your Strike account rather than requiring you to deposit Bitcoin or cash with SatStacker itself.",
    },
  ];

  return (
    <>
      <Head>
        <title>{title}</title>

        <meta
          name="description"
          content={description}
        />

        <link
          rel="canonical"
          href={canonical}
        />

        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta
          property="og:description"
          content={description}
        />
        <meta property="og:url" content={canonical} />
        <meta property="og:site_name" content="SatStacker" />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />
        <meta
          name="twitter:title"
          content={title}
        />
        <meta
          name="twitter:description"
          content={description}
        />

        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Head>

      <main className="min-h-screen bg-zinc-950 text-white">
        <article
          className="
            prose prose-invert
            lg:prose-lg
            max-w-none md:max-w-3xl
            mx-auto
            px-4 sm:px-6 lg:px-8
            py-12
            break-words
          "
        >
          <Link
            to="/blog"
            className="not-prose text-sm text-zinc-500 hover:text-orange-400 no-underline"
          >
            ← Bitcoin Guides
          </Link>

          <header className="mt-8 mb-10">
            <p className="not-prose text-xs uppercase tracking-[0.18em] text-orange-400 mb-4">
              Bitcoin Ownership Guide
            </p>

            <h1>
              Bitcoin Self-Custody: What It Means & How to Get Started
            </h1>

            <p className="lead text-zinc-300">
              Bitcoin self-custody means holding the keys that control your
              Bitcoin instead of relying on a third party to approve access.
              That gives you more control and more responsibility.
            </p>
          </header>

          <section>
            <h2>What is Bitcoin self-custody?</h2>

            <p>
              A Bitcoin wallet uses private keys to authorize transactions.
              In a self-custody setup, you control those keys yourself.
            </p>

            <p>
              By contrast, when Bitcoin is held in a custodial account, the
              service provider controls the keys and keeps track of your
              account balance on its own systems. You generally ask the
              custodian to process withdrawals on your behalf.
            </p>
          </section>

          <section>
            <h2>Custodial vs self-custodial Bitcoin</h2>

            <div className="not-prose overflow-x-auto my-6">
              <div className="min-w-[620px] overflow-hidden rounded-2xl border border-zinc-800">
                {[
                  ["", "Custodial account", "Self-custody wallet"],
                  ["Who controls the keys?", "The provider", "You"],
                  ["Password recovery", "Often available through the provider", "Depends on your own backup"],
                  ["Withdrawals", "Provider processes them", "You authorize them"],
                  ["Third-party dependency", "Higher", "Lower"],
                  ["Personal responsibility", "Lower", "Higher"],
                ].map((row, rowIndex) => (
                  <div
                    key={row[0] || "header"}
                    className={`grid grid-cols-3 ${
                      rowIndex === 0
                        ? "bg-orange-500/10"
                        : rowIndex % 2 === 0
                        ? "bg-zinc-900/50"
                        : "bg-zinc-950"
                    } ${
                      rowIndex < 5
                        ? "border-b border-zinc-800"
                        : ""
                    }`}
                  >
                    {row.map((cell, cellIndex) => (
                      <div
                        key={`${rowIndex}-${cellIndex}`}
                        className={`px-4 py-3 text-sm leading-6 ${
                          rowIndex === 0 || cellIndex === 0
                            ? "font-semibold text-zinc-200"
                            : "text-zinc-400"
                        }`}
                      >
                        {cell}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section>
            <h2>Why do people choose self-custody?</h2>

            <p>
              The main reason is control. A self-custody wallet lets you
              authorize transactions without asking an exchange or brokerage
              to release the Bitcoin first.
            </p>

            <p>
              Self-custody can also reduce exposure to certain third-party
              risks, such as an exchange freezing withdrawals or becoming
              unavailable. But it replaces those risks with different ones:
              you are responsible for protecting your keys and backups.
            </p>
          </section>

          <section>
            <h2>What are the risks of self-custody?</h2>

            <p>
              Self-custody is not automatically safer in every situation.
              It changes who is responsible for security.
            </p>

            <div className="not-prose grid md:grid-cols-2 gap-4 my-6">
              {[
                {
                  title: "Lost recovery backup",
                  body: "If you lose both the wallet access and the recovery backup, there may be no company that can restore the funds for you.",
                },
                {
                  title: "Exposed recovery phrase",
                  body: "Anyone who obtains a valid recovery phrase may be able to recreate the wallet and move its Bitcoin.",
                },
                {
                  title: "Sending to the wrong address",
                  body: "Bitcoin transactions are generally not reversible, so addresses and transaction details should be checked carefully.",
                },
                {
                  title: "Poor backup practices",
                  body: "A backup stored only in one place can be vulnerable to loss, theft, fire, water damage, or accidental disposal.",
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-5"
                >
                  <h3 className="text-base font-semibold text-white mt-0 mb-2">
                    {item.title}
                  </h3>

                  <p className="text-sm text-zinc-400 leading-6 m-0">
                    {item.body}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2>Software wallets vs hardware wallets</h2>

            <p>
              Both software and hardware wallets can be self-custodial. The
              difference is mainly where and how the keys are stored and used.
            </p>

            <p>
              Software wallets run on a phone or computer and can be
              convenient for smaller balances and regular use. Hardware
              wallets are dedicated devices designed to keep signing keys
              separated from a general-purpose computer or phone.
            </p>

            <p>
              The right setup depends on the amount involved, how often you
              expect to move Bitcoin, and how comfortable you are managing
              backups and security procedures.
            </p>
          </section>

          <section>
            <h2>How to start self-custody carefully</h2>

            <ol>
              <li>
                <strong>Choose a wallet you understand.</strong> Learn how its
                backup and recovery process works before moving a meaningful
                amount of Bitcoin.
              </li>

              <li>
                <strong>Create the wallet in a private environment.</strong>
                Follow the wallet provider's official setup instructions.
              </li>

              <li>
                <strong>Protect the recovery backup.</strong> Do not post it,
                email it to yourself, or enter it into websites that ask for it.
              </li>

              <li>
                <strong>Verify the receiving address.</strong> Check the
                address carefully before sending Bitcoin.
              </li>

              <li>
                <strong>Start with a small test transaction.</strong> Confirm
                that you can receive and access the funds before moving a
                larger amount.
              </li>

              <li>
                <strong>Think about backup redundancy.</strong> Your setup
                should account for device failure, loss, theft, and physical
                damage without making the backup easy for someone else to
                access.
              </li>
            </ol>
          </section>

          <section>
            <h2>What is a Bitcoin recovery phrase?</h2>

            <p>
              Many self-custody wallets provide a recovery phrase during
              setup. That phrase can be used to recreate access to the wallet
              in compatible software or hardware.
            </p>

            <p>
              Because the recovery phrase can be extremely powerful, it should
              be treated more like a master key than an ordinary password.
              Legitimate support staff should not need you to reveal it to
              troubleshoot a normal account issue.
            </p>
          </section>

          <section>
            <h2>How self-custody fits with Bitcoin DCA</h2>

            <p>
              Buying Bitcoin and storing Bitcoin are related but separate
              decisions. A recurring purchase strategy determines how Bitcoin
              is acquired; custody determines who controls the keys afterward.
            </p>

            <p>
              Someone can use a DCA strategy while leaving Bitcoin with a
              custodian, withdraw periodically to a self-custody wallet, or
              use another custody workflow that fits their needs.
            </p>

            <Link
              to="/bitcoin-dca"
              className="text-orange-400 hover:underline"
            >
              Read the How to DCA Bitcoin guide →
            </Link>
          </section>

          <section className="not-prose my-10 rounded-2xl border border-orange-500/20 bg-orange-500/5 p-6">
            <p className="text-xs uppercase tracking-[0.18em] text-orange-400 font-semibold mb-2">
              Where SatStacker fits
            </p>

            <h2 className="text-2xl font-bold text-white mt-0 mb-3">
              SatStacker does not custody your funds
            </h2>

            <p className="text-zinc-400 leading-7 mb-4">
              SatStacker is an automation layer that connects to your
              existing Strike account. You do not deposit cash or Bitcoin
              with SatStacker itself.
            </p>

            <p className="text-zinc-400 leading-7 mb-5">
              If self-custody is part of your Bitcoin plan, the custody step
              should be considered separately from your recurring-buy
              schedule and configured according to the wallet and withdrawal
              workflow you use.
            </p>

            <Link
              to="/blog/best-bitcoin-apps-2026"
              className="inline-block rounded-xl border border-zinc-700 px-5 py-3 font-semibold text-white no-underline hover:border-orange-500"
            >
              Compare Bitcoin DCA Apps →
            </Link>
          </section>

          <section>
            <h2>Common self-custody mistakes to avoid</h2>

            <ul>
              <li>
                Typing a recovery phrase into an unexpected website or form.
              </li>
              <li>
                Keeping the only backup on the same phone or computer as the
                wallet.
              </li>
              <li>
                Moving a large balance before testing the setup with a smaller
                transaction.
              </li>
              <li>
                Assuming a hardware wallet removes the need for careful backup
                and phishing protection.
              </li>
              <li>
                Making the setup so complicated that you cannot confidently
                recover it yourself.
              </li>
            </ul>
          </section>

          <section>
            <h2>Bitcoin self-custody FAQ</h2>

            <div className="not-prose space-y-4 my-6">
              {faqItems.map((item) => (
                <div
                  key={item.q}
                  className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5"
                >
                  <h3 className="text-base font-semibold text-white mb-2">
                    {item.q}
                  </h3>

                  <p className="text-zinc-400 leading-7 m-0">
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2>Key takeaway</h2>

            <p>
              Self-custody gives you direct control of the keys that authorize
              Bitcoin transactions. That reduces reliance on a custodian, but
              it also means you are responsible for protecting access and
              recovery information.
            </p>

            <p>
              A good self-custody setup is one you understand, can back up
              reliably, and can recover without depending on guesswork.
            </p>
          </section>

          <hr />

          <div className="not-prose mt-8 space-y-3">
            <p className="font-semibold text-white">
              Continue learning
            </p>

            <Link
              to="/bitcoin-dca"
              className="block text-orange-400 hover:underline"
            >
              How to DCA Bitcoin →
            </Link>

            <Link
              to="/articles/dca-vs-lump-sum"
              className="block text-orange-400 hover:underline"
            >
              Bitcoin DCA vs Lump Sum →
            </Link>

            <Link
              to="/smart-dca"
              className="block text-orange-400 hover:underline"
            >
              Smart DCA Guide →
            </Link>

            <Link
              to="/calculator"
              className="block text-orange-400 hover:underline"
            >
              Bitcoin DCA Calculator →
            </Link>

            <Link
              to="/blog/best-bitcoin-app"
              className="block text-orange-400 hover:underline"
            >
              Best Bitcoin App for Beginners →
            </Link>
          </div>
        </article>
      </main>
    </>
  );
}
